import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, List, Tag, Typography, Button, Space, Avatar, Progress, Empty } from 'antd';
import {
  DashboardOutlined,
  ReloadOutlined,
  BankOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  RiseOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import MainLayout from '../../layouts/MainLayout';
import { superAdminDashboardApi, normalizeSuperAdminDashboardBody } from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import { DashboardSkeleton } from '../../components/SkeletonLoaders';

const { Text } = Typography;

const PACKAGE_TYPE_LABEL = {
  monthly: 'ماهانه',
  annual: 'سالانه',
  lifetime: 'مادام‌العمر',
};

const toneClassMap = {
  emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
  blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300',
  violet: 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300',
};

const StatCard = ({ title, value, icon, tone = 'blue' }) => (
  <Card className="border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all bg-white dark:bg-gray-800 h-full">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
          {title}
        </div>
        <div
          className="mt-1 text-2xl font-extrabold text-gray-800 dark:text-gray-100 tracking-tight"
          style={{ fontFamily: 'CustomFont' }}
        >
          {value}
        </div>
      </div>
      <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${toneClassMap[tone] || toneClassMap.blue}`}>
        {icon}
      </div>
    </div>
  </Card>
);

const fmtNum = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return v ?? '—';
  return n.toLocaleString('fa-IR');
};

const CompanyRow = ({ company }) => {
  const pkgName = company?.package?.name;
  const pkgType = PACKAGE_TYPE_LABEL[company?.package_type] || company?.package_type || '—';
  return (
    <List.Item className="!px-0">
      <List.Item.Meta
        avatar={
          <Avatar src={company?.logo_url} icon={<BankOutlined />} className="flex-shrink-0 bg-gray-100 dark:bg-gray-700" />
        }
        title={
          <span className="font-medium text-gray-800 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
            {company?.company_name || '—'}
          </span>
        }
        description={
          <Space size={[4, 4]} wrap className="mt-0.5">
            {pkgName ? <Tag color="blue">{pkgName}</Tag> : null}
            <Tag>{pkgType}</Tag>
          </Space>
        }
      />
    </List.Item>
  );
};

const TopCompanyRow = ({ company }) => (
  <List.Item className="!px-0">
    <List.Item.Meta
      avatar={
        <Avatar src={company?.logo_url} icon={<BankOutlined />} className="flex-shrink-0 bg-gray-100 dark:bg-gray-700" />
      }
      title={
        <span className="font-medium text-gray-800 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
          {company?.company_name || '—'}
        </span>
      }
      description={
        <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
          <span>کاربران: {fmtNum(company?.users_count)}</span>
          <span>کارمند: {fmtNum(company?.employees_count)}</span>
          <span>مشتری: {fmtNum(company?.clients_count)}</span>
        </div>
      }
    />
  </List.Item>
);

const SectionCard = ({ title, extra, children, className = '' }) => (
  <Card
    title={<span style={{ fontFamily: 'CustomFont' }}>{title}</span>}
    extra={extra}
    className={`border border-gray-100 dark:border-gray-700 shadow-sm dark:bg-gray-800 h-full ${className}`}
    styles={{ body: { paddingTop: 12 } }}
  >
    {children}
  </Card>
);

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superAdminDashboardApi.get();
      setData(normalizeSuperAdminDashboardBody(res));
    } catch (error) {
      setData(null);
      handleApiError(error, 'خطا در دریافت داشبورد');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = data?.company_stats || {};
  const statCards = useMemo(
    () => [
      {
        title: 'کل شرکت‌ها',
        value: fmtNum(stats.total),
        icon: <BankOutlined />,
        tone: 'emerald',
      },
      {
        title: 'فعال',
        value: fmtNum(stats.active),
        icon: <CheckCircleOutlined />,
        tone: 'blue',
      },
      {
        title: 'غیرفعال',
        value: fmtNum(stats.inactive),
        icon: <CloseCircleOutlined />,
        tone: 'amber',
      },
      {
        title: 'منقضی‌شده',
        value: fmtNum(stats.expired),
        icon: <ClockCircleOutlined />,
        tone: 'rose',
      },
      {
        title: 'بسته‌های تعریف‌شده',
        value: fmtNum(data?.total_packages),
        icon: <AppstoreOutlined />,
        tone: 'violet',
      },
    ],
    [stats, data?.total_packages],
  );

  const registrationChartData = useMemo(() => {
    const chart = data?.registrations_chart;
    if (!chart?.labels?.length) return [];
    return chart.labels.map((label, i) => ({
      name: label,
      count: Number(chart.values?.[i] ?? 0),
      fill: chart.colors?.[0] || '#005E4D',
    }));
  }, [data?.registrations_chart]);

  const packageRows = data?.package_company_count || [];
  const packageMax = useMemo(
    () => Math.max(...packageRows.map((p) => Number(p.companies_count) || 0), 1),
    [packageRows],
  );

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 dark:bg-violet-900/25">
                <CrownOutlined className="text-lg sm:text-xl text-violet-600 dark:text-violet-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 truncate">
                  داشبورد سوپرادمین
                </h1>
                <Text type="secondary" className="text-xs sm:text-sm">
                  نمای کلی شرکت‌ها، اشتراک‌ها و رشد ثبت‌نام
                </Text>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={fetchData}>
              بروزرسانی
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50 space-y-6">
            {loading ? (
              <DashboardSkeleton cards={5} />
            ) : !data ? (
              <Empty description="داده‌ای دریافت نشد" />
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                  {statCards.map((s) => (
                    <StatCard key={s.title} {...s} />
                  ))}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <SectionCard title="نمودار ثبت‌نام شرکت‌ها" className="xl:col-span-2">
                    {registrationChartData.length ? (
                      <div className="h-[280px] w-full min-w-0" dir="ltr">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={registrationChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} className="opacity-30" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fontFamily: 'CustomFont' }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={36} />
                            <Tooltip
                              formatter={(value) => [fmtNum(value), 'ثبت‌نام']}
                              contentStyle={{ borderRadius: 10, direction: 'rtl', fontFamily: 'CustomFont' }}
                              labelStyle={{ direction: 'rtl', fontFamily: 'CustomFont' }}
                            />
                            <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
                              {registrationChartData.map((entry, index) => (
                                <Cell key={`reg-${index}`} fill={entry.fill} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="داده‌ای برای نمودار نیست" />
                    )}
                  </SectionCard>

                  <SectionCard title="شرکت‌ها به تفکیک بسته">
                    {packageRows.length ? (
                      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-1">
                        {packageRows.map((pkg) => {
                          const count = Number(pkg.companies_count) || 0;
                          const percent = Math.round((count / packageMax) * 100);
                          return (
                            <div key={pkg.id}>
                              <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                                <span className="truncate font-medium text-gray-700 dark:text-gray-200">{pkg.name}</span>
                                <Tag color={count > 0 ? 'blue' : 'default'}>{fmtNum(count)}</Tag>
                              </div>
                              <Progress percent={percent} showInfo={false} size="small" strokeColor="#005E4D" />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="بسته‌ای ثبت نشده" />
                    )}
                  </SectionCard>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  <SectionCard
                    title="آخرین شرکت‌های ثبت‌شده"
                    extra={<RiseOutlined className="text-emerald-500" />}
                  >
                    <List
                      size="small"
                      dataSource={data.recent_registered_companies || []}
                      locale={{ emptyText: 'شرکتی ثبت نشده' }}
                      renderItem={(c) => <CompanyRow company={c} />}
                    />
                  </SectionCard>

                  <SectionCard title="آخرین اشتراک‌ها" extra={<TeamOutlined className="text-blue-500" />}>
                    <List
                      size="small"
                      dataSource={data.recent_subscriptions || []}
                      locale={{ emptyText: 'اشتراکی ثبت نشده' }}
                      renderItem={(c) => <CompanyRow company={c} />}
                    />
                  </SectionCard>

                  <SectionCard
                    title="بیشترین تعداد کاربر"
                    extra={<DashboardOutlined className="text-violet-500" />}
                    className="lg:col-span-2 xl:col-span-1"
                  >
                    <List
                      size="small"
                      dataSource={data.top_companies_user_count || []}
                      locale={{ emptyText: 'داده‌ای موجود نیست' }}
                      renderItem={(c) => <TopCompanyRow company={c} />}
                    />
                  </SectionCard>
                </div>

                <SectionCard title="شرکت‌های با لایسنس منقضی‌شده اخیر">
                  {(data.recent_licence_expired_companies || []).length ? (
                    <List
                      size="small"
                      dataSource={data.recent_licence_expired_companies}
                      renderItem={(c) => <CompanyRow company={c} />}
                    />
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-white/60 px-4 py-8 text-center dark:border-gray-600 dark:bg-gray-900/20">
                      <CheckCircleOutlined className="mb-2 text-2xl text-emerald-500" />
                      <div className="text-sm text-gray-600 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
                        شرکتی با لایسنس منقضی‌شده در بازه اخیر ثبت نشده است
                      </div>
                    </div>
                  )}
                </SectionCard>
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperAdminDashboard;
